import { Component, Inject, OnInit, OnDestroy, ViewChild, ElementRef, TemplateRef } from '@angular/core';
import { jwtDecode } from 'jwt-decode';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { ApiService } from 'src/app/services/apiService/api.service';
import { SuccessfullyUploadedComponent } from '../successfully-uploaded/successfully-uploaded.component';
import { ErrorUploadedComponent } from '../error-uploaded/error-uploaded.component';
import { UploadedFilesDialogComponent } from '../uploaded-files-dialog/uploaded-files-dialog.component';
import * as XLSX from 'xlsx';
import { environment } from 'src/environments/environment';
import { Subject, takeUntil } from 'rxjs';
import { ContentService } from '../../services/content.service';
import { TravelerManagement } from 'src/app/services/dataLayer/CAM/traveler-management.service';
import { CamService } from 'src/app/services/camService/cam.service';
import { Branch } from 'src/app/models/interface/CAM/CompanyProfileInterface';
import { Department, Designation } from 'src/app/models/interface/CAM/TravellerInterface';
import { MatIconButton, MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { NgIf, NgClass } from '@angular/common';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
    selector: 'app-bulk-upload-dialog',
    templateUrl: './bulk-upload-dialog.component.html',
    styleUrls: ['./bulk-upload-dialog.component.scss'],
    imports: [ReactiveFormsModule, MatIconButton, MatIcon, CdkScrollable, MatDialogContent, MatButton, NgIf, MatDialogActions, MatCheckbox, NgClass, MatProgressSpinner]
})
export class BulkUploadDialogComponent implements OnInit, OnDestroy {
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild('processingTemplate') private processingTemplate!: TemplateRef<any>;
  processingModalRef!: MatDialogRef<any>;

  uploadError = false;
  fileTooLarge = false;
  maxFileSize = 10 * 1024 * 1024;
  CorrectTravellersData: any;
  BulkUploadForm!: FormGroup;
  isCheckboxChecked: boolean = false;
  fileSelected: boolean = false;
  uploadedFileName: string = '';
  isFileUploaded = false;
  IsFormValidd: boolean = false;
  NotUploadedData: any;

  private readonly destroy$ = new Subject<void>();
  content:any;
  masterBranches: Branch[] = [];
  masterDepartments: Department[] = [];
  masterDesignations: Designation[] = [];
  masterBranchesMap = new Map<string, any>();
  masterDepartmentsMap = new Map<string, any>();
  masterDesignationsMap = new Map<string, any>();
  isLoadingMasterData: boolean = false;
  predefinedHeaders = ['First Name', 'Last Name', 'Employee Code', 'Email', 'Department Name', 'Office Location Name', 'Designation Name', 'Employee Type'];


  constructor(
    public _MatDialogRef: MatDialogRef<BulkUploadDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { serviceLists: any },
    public _ApiService: ApiService,
    private formBuilder: FormBuilder,
    private readonly _MatDialog: MatDialog,
    private readonly contentService:ContentService,
    private readonly travelerDataLayer:TravelerManagement,
    private readonly _CamService: CamService
  ) {
    // Initialize your form
    this.BulkUploadForm = this.formBuilder.group({
      // You can add any form controls here if necessary   
    });
  }

  ngOnInit(): void {
    this.contentService.content$.pipe(takeUntil(this.destroy$)).subscribe((res:any)=>{
      this.content = res;
    });
    this.travelerDataLayer.bulkUploadAction('Popup_pageview',' Popup_pageview');
  }

  async fetchMasterData() {
    this.isLoadingMasterData = true;
    try {
      const [branchesRes, departmentsRes, designationsRes] = await Promise.all([
        this._CamService.GetBranchInformation(),
        this._CamService.getDepartmentDetails(),
        this._CamService.getDesignationDetails()
      ]);
      this.masterBranches = branchesRes?.data || [];
      this.masterDepartments = departmentsRes?.data || [];
      this.masterDesignations = designationsRes?.data || [];

      this.masterBranchesMap.clear();
      this.masterBranches.forEach((b: any) => {
        const name = b.corpBranchName || b.branchName || b.name;
        if (name) this.masterBranchesMap.set(name.trim().toLowerCase(), b);
      });

      this.masterDepartmentsMap.clear();
      this.masterDepartments.forEach((d: any) => {
        const name = d.departmentName || d.name;
        if (name) this.masterDepartmentsMap.set(name.trim().toLowerCase(), d);
      });

      this.masterDesignationsMap.clear();
      this.masterDesignations.forEach((d: any) => {
        const name = d.designation || d.designationName || d.name;
        if (name) this.masterDesignationsMap.set(name.trim().toLowerCase(), d);
      });
    } catch (err) {
      console.error('Error fetching master data:', err);
    } finally {
      this.isLoadingMasterData = false;
      this.checkFormValidity();
    }
  }
  readonly cacheBuster = new Date().getTime();

  get sanitizedDesc(): string {
    const desc = this.content?.['b2b.managetraveler.uploadbulktravelersdesc'] || '';
    if (!desc) return '';
    return desc
      .replace('<ul>', '<ul class="list-style">')
      .replace('CSV sample', `<a href="assets/files/Bulkuploadfile.csv?v=${this.cacheBuster}" download class="csv-sample-link">CSV Sample</a>`);
  }

  onDocsListClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target.tagName === 'A' && target.classList.contains('csv-sample-link')) {
      this.viewSampleClicked();
    }
  }
  onFileSelected(event: any): void {
    this.travelerDataLayer.bulkUploadAction('Choose_file','Choose file button');
     //  Call GTM error service
    this.travelerDataLayer.uploadError('File upload error');
    const file = event.target.files[0];
    if (file) {
      // Reset error flags
      this.uploadError = false;
      this.fileTooLarge = false;
      this.fileSelected = true;
      this.isFileUploaded = true;
      this.uploadedFileName = file.name;
      const ext = file.name.split('.').pop()?.toLowerCase();
      const allowedExt = ['csv', 'xls', 'xlsx'];

      if (!ext || !allowedExt.includes(ext)) {
        this.uploadError = true;
        this.fileSelected = false;
        this.isFileUploaded = false;
        this.uploadedFileName = '';
        if (this.fileInput && this.fileInput.nativeElement) {
          this.fileInput.nativeElement.value = '';
        }
        return;
      }


      // Check if file size exceeds the limit
      if (file.size > this.maxFileSize) {
        this.fileTooLarge = true;
        this.uploadError = true;
        this.fileSelected = false;
        this.isFileUploaded = false;
        this.uploadedFileName = '';
        if (this.fileInput && this.fileInput.nativeElement) {
          this.fileInput.nativeElement.value = '';
        }
        return;
      }
      this.readCSVFile(file);
    }
  }



viewSampleClicked(){
  console.log('entered in to view sample clicked')
this.travelerDataLayer.bulkUploadAction('CSV_sample','CSV_sample');
}

readCSVFile(file: File): void {
  const ext = file.name.split('.').pop()?.toLowerCase();
  const reader = new FileReader();

  reader.onload = (e: any) => {

    let empData: any[] = [];
    let missingFields: any[] = [];

    // ✅ CSV
    if (ext === 'csv') {
      const fileContent = e.target.result as string;
      const parsedData = this.parseCSV(fileContent);
      empData = parsedData.empData;
      missingFields = parsedData.missingFields;
    }

    // ✅ EXCEL
    else if (ext === 'xlsx' || ext === 'xls') {
      const data = new Uint8Array(e.target.result);
      const workbook = XLSX.read(data, { type: 'array' });

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      empData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

      // 🔴 SAME header validation as CSV
      const headers = Object.keys(empData[0] || []);
      const predefinedHeaders = this.predefinedHeaders;

      const missingHeaders = predefinedHeaders.filter(
        h => !headers.includes(h)
      );
      const extraHeaders = headers.filter(
        h => !predefinedHeaders.includes(h) && h.trim() !== ''
      );

      if (missingHeaders.length > 0 || extraHeaders.length > 0) {
        const errorMsg = [];
        if (missingHeaders.length > 0) errorMsg.push(`Missing headers: ${missingHeaders.join(', ')}`);
        if (extraHeaders.length > 0) errorMsg.push(`Invalid extra headers: ${extraHeaders.join(', ')}`);
        missingFields = [{ error: errorMsg.join('. ') }];
        empData = [];
      }
    }

    if (missingFields.length > 0 && missingFields[0].error) {
      this.showFileInvalidError();
      this.fileSelected = false;
      this.isFileUploaded = false;
      this.uploadError = false;
      this.fileTooLarge = false;
      this.uploadedFileName = '';
      if (this.fileInput && this.fileInput.nativeElement) {
        this.fileInput.nativeElement.value = '';
      }
      this.checkFormValidity();
      return;
    }

    this.NotUploadedData = missingFields;
    this.CorrectTravellersData = empData;
      

    if(this.CorrectTravellersData.length> environment.maxTravellerUpload)
    {
       this.fileTooLarge = true;
        this.fileSelected = false;
        this.isFileUploaded = false;
        this.uploadedFileName = '';
        return;
    }

    // Only fetch API data when template validation succeeds
    this.fetchMasterData();
    this.checkFormValidity();
  };

  // 🔴 READ MODE
  if (ext === 'csv') {
    reader.readAsText(file);
  } else {
    reader.readAsArrayBuffer(file);
  }
}

parseCSV(csv: string): { empData: any[], missingFields: any[] } {
  const rows = csv.split(/\r?\n/);

  if (!rows.length) {
    return { empData: [], missingFields: [] };
  }

  const headers = rows[0].split(',').map(h => h.trim());
  const predefinedHeaders = this.predefinedHeaders;

  const missingHeaders = predefinedHeaders.filter(h => !headers.includes(h));
  const extraHeaders = headers.filter(h => !predefinedHeaders.includes(h) && h !== '');
  const duplicateHeaders = headers.filter((h, i) => h !== '' && headers.indexOf(h) !== i);

  if (missingHeaders.length > 0 || extraHeaders.length > 0 || duplicateHeaders.length > 0) {
    const errorMsg = [];
    if (missingHeaders.length > 0) errorMsg.push(`Missing headers: ${missingHeaders.join(', ')}`);
    if (extraHeaders.length > 0) errorMsg.push(`Invalid extra headers: ${extraHeaders.join(', ')}`);
    if (duplicateHeaders.length > 0) errorMsg.push(`Duplicate headers: ${duplicateHeaders.join(', ')}`);

    return {
      empData: [],
      missingFields: [{ error: errorMsg.join('. ') }]
    };
  }

  const parseRow = (row: string): string[] => {
    const values: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < row.length; i++) {
      const ch = row[i];

      if (ch === '"' && row[i - 1] !== '\\') {
        inQuotes = !inQuotes;
      } else if (ch === ',' && !inQuotes) {
        values.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }

    values.push(current.trim());
    return values;
  };

  const empData: any[] = [];
  const missingFields: any[] = [];

  rows.slice(1).forEach(row => {

    // 🔴 FINAL GUARD — handles "", empty line, spaces
    if (!row || row.trim() === '' || row.trim() === '""') {
      return;
    }

    const values = parseRow(row);

    const rowObj: any = {};
    headers.forEach((h, i) => {
      rowObj[h] = values[i] || '';
    });

    // 🔴 Skip rows where ALL fields are empty
    const hasRealData = Object.values(rowObj).some(
      v => v && String(v).trim() !== ''
    );

    if (!hasRealData) return;

    // ✅ Add company code only to real rows
    rowObj['Company_Code'] = 'saa';
    rowObj['@SubAgent_ID'] = '1234';
    empData.push(rowObj);
  });

  this.checkFormValidity();
  return { empData, missingFields };
}



  private openProcessingPopup(): void {
    this.processingModalRef = this._MatDialog.open(this.processingTemplate, {
      panelClass: 'custom-popup-gen-processing-popup',
      disableClose: true,
      autoFocus: false
    });
  }

  private closeProcessingPopup(): void {
    this.processingModalRef?.close();
  }

  private showFileInvalidError(): void {
    this._MatDialog.open(ErrorUploadedComponent, {
      disableClose: true,
      panelClass: 'figma-dialog-panel',
      data: { showErrorIcon: true, ctaText: 'Try again' }
    });
  }

  UploadTraveller() {
    if (!this.isFormValid()) {
      console.error('Form is not valid');
      this.travelerDataLayer.fileError();
      return;
    }

    this.travelerDataLayer.bulkUploadAction('Upload','Upload');
    this.travelerDataLayer.uploadError('File upload error');

    // Empty file: show the "File could not be uploaded" error instead of hitting the backend
    if (!this.CorrectTravellersData || this.CorrectTravellersData.length === 0) {
      this.showFileInvalidError();
      return;
    }

    const finalPayload: any[] = [];
    let rowNum = 1;

    for (const row of this.CorrectTravellersData) {
      rowNum++; // 1 for header, so first data row is 2
      const empCode = String(row['Employee Code'] || '').trim();
      const email = String(row['Email'] || '').trim();
      const deptName = String(row['Department Name'] || '').trim();
      const branchName = String(row['Office Location Name'] || '').trim();
      const desigName = String(row['Designation Name'] || '').trim();

      // Default Employee Type logic
      let empType = String(row['Employee Type'] || '').trim();
      if (!empType) {
        empType = 'Employee';
      } else {
        const lowerEmpType = empType.toLowerCase();
        if (lowerEmpType === 'admin') empType = 'Admin';
        else if (lowerEmpType === 'travel arranger') empType = 'Travel Arranger';
        else if (lowerEmpType === 'employee') empType = 'Employee';
        else empType = 'Employee'; // Default to Employee if invalid
      }

      const foundBranch = this.masterBranchesMap.get(branchName.toLowerCase());
      const foundDept = this.masterDepartmentsMap.get(deptName.toLowerCase());
      const foundDesig = this.masterDesignationsMap.get(desigName.toLowerCase());

      let mappedEmpType = 2; // Default to Employee
      if (empType === 'Admin') mappedEmpType = 0;
      else if (empType === 'Travel Arranger') mappedEmpType = 1;

      // Prepare the payload object
      finalPayload.push({
        FirstName: String(row['First Name'] || '').trim(),
        LastName: String(row['Last Name'] || '').trim(),
        EmployeeCode: empCode,
        EmailAddress: email,
        DepartmentId: foundDept ? (foundDept.departmentId || foundDept.id) : -1,
        BranchId: foundBranch ? (foundBranch.corpBranchId || foundBranch.branchId || foundBranch.id) : -1,
        DesignationId: foundDesig ? (foundDesig.designationId || foundDesig.id) : -1,
        EmployeeType: mappedEmpType,
        RowNumber: rowNum
      });
    }

    const qlAccessToken = localStorage.getItem('QlAccessToken') || sessionStorage.getItem('QlAccessToken') || '';
    let decodedToken: any = {};
    if (qlAccessToken) {
      try {
        decodedToken = jwtDecode(qlAccessToken);
      } catch (e) {
        console.error('Invalid token', e);
      }
    }

    const payload = {
      B2BId: decodedToken?.subAgentId || '',
      B2BCompanyId: decodedToken?.companyId || '',
      B2BName: decodedToken?.companyName || '',
      Travellers: finalPayload
    };

    this.OnsaveTravellers(payload);
  }

async OnsaveTravellers(TravellerPayload: any) {
  this.openProcessingPopup();
  try {
    const response = await this._ApiService.BulTravellerUpdate(TravellerPayload);
    this.closeProcessingPopup();

    if (!response) {
      return;
    }
    // this.travelerDataLayer.traveler_add_success();
    this._MatDialogRef.close();

    let responseData = response.data !== undefined ? response.data : response;
    if (typeof responseData === 'string') {
      try {
        responseData = JSON.parse(responseData);
      } catch(e) {
        console.error('Failed to parse response.data', e);
      }
    }

    const insertedCount = responseData?.InsertedCount || 0;
    const failedCount = responseData?.FailedCount || 0;
    const failedUsers = responseData?.FailedUsers || [];
    const totalRecords = insertedCount + failedCount;

    if (failedCount > 0) {
      const isAllFailed = insertedCount === 0;
      this.travelerDataLayer.someUserUploadError(`${failedCount} out of ${totalRecords} profiles were not uploaded.`);
      
      const dialogRef = this._MatDialog.open(ErrorUploadedComponent, {
        disableClose: true,
        panelClass: 'figma-dialog-panel',
        data: {
          title: isAllFailed ? 'All profiles failed to upload' : 'Some of the profiles could not be uploaded',
          showViewBtn: true,
          failedCount: failedCount,
          totalRecords: totalRecords
        }
      });

      dialogRef.afterClosed().subscribe((result: any) => {
        if (result && result.action === 'view') {
          this._MatDialog.open(UploadedFilesDialogComponent, {
            disableClose: true,
            panelClass: 'figma-dialog-panel-large',
            width: '680px',
            maxWidth: '95vw',
            data: {
              failedUsers: failedUsers,
              failedCount: failedCount,
              totalRecords: totalRecords
            }
          });
        }
      });
    } else {
      // All records successful
      this.travelerDataLayer.traveler_add_success('Upload');
      this._MatDialog.open(SuccessfullyUploadedComponent, {
        disableClose: true
      });
    }

  } catch (error: any) {
    this.closeProcessingPopup();
    console.error('Error Registering Travellers:', error);
    if(this.fileTooLarge){
      this.travelerDataLayer.uploadError(this.content?.['b2b.managetraveler.ubtfileexceedslimit']);
    }
    if(this.uploadError){
      this.travelerDataLayer.uploadError(this.content?.['b2b.managetraveler.ubtfileuploaderror']);
    }
    this._MatDialogRef.close();

    let errorMessage = undefined;
    // If backend sends a specific error message
    if (error?.error?.message || error?.error?.Message) {
      errorMessage = error.error.message || error.error.Message;
    } else if (error?.message) {
      errorMessage = error.message;
    }

    this._MatDialog.open(ErrorUploadedComponent, { 
      disableClose: true,
      panelClass: 'figma-dialog-panel',
      data: errorMessage ? { message: errorMessage, title: 'Upload Failed' } : null
    });
  }
}


  CloseDialog(): void {
    this.travelerDataLayer.bulkUploadAction('Close',' Cancel');
    this._MatDialogRef.close();
  }


  isFormValid(): boolean {
    // Check if file uploaded, checkbox checked, form is valid, and master data is not loading
    return this.isFileUploaded && this.isCheckboxChecked && this.BulkUploadForm.valid && !this.isLoadingMasterData;
  }

  onCheckboxChanged(event: any) {
    this.travelerDataLayer.bulkUploadAction('Confirm_checkbox',event.checked);
    this.isCheckboxChecked = event.checked; // Update checkbox state
    this.checkFormValidity(); // Check form validity whenever checkbox is toggled
  }

  checkFormValidity() {
    // Set IsFormValid to true if both conditions are met
    this.IsFormValidd = this.isFileUploaded && this.isCheckboxChecked;
  }
  ngOnDestroy(){
    this.destroy$.next();
    this.destroy$.complete();
  }
}
